import Dot from '@contently/dot-tree';
import Entitlement from './Entitlement';
import {
  instanceOfAnyOrError
} from './utils/instance-checker';

/**
 * Represents a matcch result of Query
 */
class Match {
  match = null;

  entitlement = null;

  static make(match, entitlement) {
    return Object.assign(new Match(), {
      match,
      entitlement
    });
  }
}

/**
 * The result of a query - contains `matches` and `missing` items
 */
class Result {
  missing = null; // array

  static make(matches, missing) {
    return Object.assign(new Result(), {
      matches,
      missing
    });
  }
}

/**
 * Performs Queries on an Entitlements instance
 */
export default class Query {
  /**
   *
   * @param {any} value - this can be a string of dot notation,
   * an array of strings, or a fully-formed `Query` object.
   */
  static create(value) {
    if (value instanceof Query) {
      return value;
    }
    if (Array.isArray(value)) {
      return Object.assign(new Query(), {
        type: 'any',
        options: value
      });
    }
    if (value instanceof Object) {
      return Object.assign(new Query(), value);
    }
    return Object.assign(new Query(), {
      options: [value]
    });
  }

  type = 'one';

  options = [];

  /**
   *
   * @param {object} tree - the tree on which to perform this query
   * @param {function} predicate - this is the predicate to use for matching
   */
  matches(tree, predicate = () => true) {
    if (predicate == null) throw new Error('predicate is required');
    switch (this.type) {
      case 'one':
        return this.__matchOne(this.options[0], tree, predicate);
      case 'any':
        return this.__matchAny(this.options, tree, predicate);
      case 'all':
        return this.__matchAll(this.options, tree, predicate);
      default:
        throw new Error("Invalid query type. Valid types: 'one', 'any', 'all'");
    }
  }

  __matchOne(option, tree, predicate) {
    const entitlement = this.__findRecursive(option, tree);
    let result;
    if (entitlement) {
      instanceOfAnyOrError(entitlement, Entitlement, 'entitlement');
      if (predicate(entitlement)) {
        result = Result.make([Match.make(option, entitlement)], []);
      } else {
        result = Result.make([], []);
      }
    } else {
      result = Result.make([], [option]);
    }
    return result;
  }

  __matchAny(options, tree, predicate) {
    return options.map((o) => {
      const result = this.__matchOne(o, tree, predicate);
      return result;
    }).reduce((all, curr) => Object.assign(Result.make([], []), {
      matches: [
        ...(all.matches || []),
        ...(curr.matches || [])
      ],
      missing: [...(all.missing || []), ...(curr.missing || [])]
    }), new Result());
  }

  __matchAll(options, tree, predicate) {
    let result = this.__matchAny(options, tree, predicate);
    const matches = result.matches
      .filter((f, n, all) => all.length === options.length && f.entitlement.enabled);
    result = {
      ...result,
      matches: [...matches]
    };
    return result;
  }

  __findRecursive(name, target) {
    const container = target || this.entitlements;
    return Dot.find(name, container);
  }
}
